import { Injectable } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import { Observable, Subject } from 'rxjs';
import { filter, map, tap } from 'rxjs/operators';

export interface SiteArea {
    name: string;
    boundary: GeoJSON.Geometry;
}

export interface Site {
    id: number;
    name: string;
    description: string;
    archived: boolean;
    missionOverlapOperatingRequirement: 'INFORM' | 'ACKNOWLEDGE';
    siteAreaList: SiteArea[];
    organisation: {
        id: number;
        name: string;
    };
}

export interface CreateSiteCommand {
    organisationId: number;
    managingOrganisationId: number;
    name: string;
    description: string;
    missionOverlapOperatingRequirement: Site['missionOverlapOperatingRequirement'];
}

export interface UpdateSiteCommand {
    id: number;
    managingOrganisationId: number;
    name: string;
    description: string;
    missionOverlapOperatingRequirement: Site['missionOverlapOperatingRequirement'];
}

@Injectable({
    providedIn: 'root'
})
export class SiteService {
    private changeSource = new Subject<void>();
    change$ = this.changeSource.asObservable();

    constructor(private apollo: Apollo) {}

    findSites(
        organisationId: number,
        managingOrganisationId: number,
        includeShared: boolean,
        includeArchived = true
    ): Observable<Site[]> {
        return this.apollo
            .query<{ findSites: { results: Site[] } }>({
                query: gql`
                    query findSites($criteria: SiteCriteria!) {
                        findSites(criteria: $criteria) {
                            results {
                                id
                                name
                                description
                                archived
                                missionOverlapOperatingRequirement
                                siteAreaList {
                                    name
                                    boundary
                                }
                                organisation {
                                    id
                                    name
                                }
                            }
                        }
                    }
                `,
                variables: {
                    criteria: {
                        organisationId,
                        managingOrganisationId,
                        includeArchived,
                        includeShared
                    }
                },
                fetchPolicy: 'network-only'
            })
            .pipe(
                filter(r => !r.loading),
                map(r => r.data.findSites.results)
            );
    }

    createSite(command: CreateSiteCommand): Observable<Site> {
        return this.apollo
            .mutate<{ createSite: Site }>({
                mutation: gql`
                    mutation createSite($command: CreateSiteCommand!) {
                        createSite(command: $command) {
                            id
                            name
                            description
                            archived
                            missionOverlapOperatingRequirement
                        }
                    }
                `,
                variables: {
                    command
                }
            })
            .pipe(
                map(result => {
                    if (result?.data?.createSite != null) {
                        return result.data.createSite;
                    }
                    throw new Error('Create site mutation failed');
                }),
                tap(() => this.changeSource.next())
            );
    }

    updateSite(command: UpdateSiteCommand): Observable<Site> {
        return this.apollo
            .mutate<{ updateSite: Site }>({
                mutation: gql`
                    mutation updateSite($command: UpdateSiteCommand!) {
                        updateSite(command: $command) {
                            id
                            name
                            description
                            archived
                            missionOverlapOperatingRequirement
                        }
                    }
                `,
                variables: { command }
            })
            .pipe(
                map(result => {
                    if (result?.data?.updateSite != null) {
                        return result.data.updateSite;
                    }
                    throw new Error('Update site mutation failed');
                }),
                tap(() => this.changeSource.next())
            );
    }

    archiveSite(
        siteId: number,
        managingOrganisationId: number
    ): Observable<Site> {
        return this.apollo
            .mutate<{ archiveSite: Site }>({
                mutation: gql`
                    mutation archiveSite(
                        $siteId: Long!
                        $managingOrganisationId: Long
                    ) {
                        archiveSite(
                            siteId: $siteId
                            managingOrganisationId: $managingOrganisationId
                        ) {
                            id
                            name
                            description
                            archived
                            missionOverlapOperatingRequirement
                        }
                    }
                `,
                variables: { siteId, managingOrganisationId }
            })
            .pipe(
                map(result => {
                    if (result?.data?.archiveSite != null) {
                        return result.data.archiveSite;
                    }
                    throw new Error('Archive site mutation failed');
                }),
                tap(() => this.changeSource.next())
            );
    }

    findSiteById(
        siteId: number,
        managingOrganisationId: number
    ): Observable<Site> {
        return this.apollo
            .query<{ findSiteById: Site }>({
                query: gql`
                    query findSiteById(
                        $siteId: Long!
                        $managingOrganisationId: Long
                    ) {
                        findSiteById(
                            siteId: $siteId
                            managingOrganisationId: $managingOrganisationId
                        ) {
                            id
                            name
                            description
                            archived
                            missionOverlapOperatingRequirement
                            siteAreaList {
                                name
                                boundary
                            }
                        }
                    }
                `,
                variables: { siteId, managingOrganisationId },
                fetchPolicy: 'network-only'
            })
            .pipe(
                filter(r => !r.loading),
                map(r => r.data.findSiteById)
            );
    }

    updateSiteAreas(command: {
        siteId: number;
        managingOrganisationId: number;
        siteAreaList: { name: string; boundary: GeoJSON.Geometry }[];
    }): Observable<Site> {
        return this.apollo
            .mutate<{ updateSiteAreas: Site }>({
                mutation: gql`
                    mutation updateSiteAreas(
                        $command: UpdateSiteAreasCommand!
                    ) {
                        updateSiteAreas(command: $command) {
                            id
                            name
                            description
                            archived
                            missionOverlapOperatingRequirement
                            siteAreaList {
                                name
                                boundary
                            }
                        }
                    }
                `,
                variables: { command }
            })
            .pipe(
                map(result => {
                    if (result?.data?.updateSiteAreas != null) {
                        return result.data.updateSiteAreas;
                    }
                    throw new Error('Update site areas mutation failed');
                }),
                tap(() => this.changeSource.next())
            );
    }

    unarchiveSite(
        siteId: number,
        managingOrganisationId: number
    ): Observable<Site> {
        return this.apollo
            .mutate<{ unarchiveSite: Site }>({
                mutation: gql`
                    mutation unarchiveSite(
                        $siteId: Long!
                        $managingOrganisationId: Long
                    ) {
                        unarchiveSite(
                            siteId: $siteId
                            managingOrganisationId: $managingOrganisationId
                        ) {
                            id
                            name
                            description
                            archived
                            missionOverlapOperatingRequirement
                        }
                    }
                `,
                variables: { siteId, managingOrganisationId }
            })
            .pipe(
                map(result => {
                    if (result?.data?.unarchiveSite != null) {
                        return result.data.unarchiveSite;
                    }
                    throw new Error('Unarchive site mutation failed');
                }),
                tap(() => this.changeSource.next())
            );
    }
}
